"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useFormatter } from "@/hooks/use-formatter"
import { IconAlertTriangle } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { AppointmentConflict, AppointmentConflictPayload } from "@/lib/appointments-api";

const MIN_REASON = 10;

/**
 * Appointments rebuild 84-05 (D3): the 409 a booking gets when it lands on the
 * doctor's or the patient's other visit. Lists the clashes; when the backend
 * says the actor may override, asks for a reason and hands it back so the
 * caller re-sends with override_conflicts + override_reason.
 */
export function ConflictOverrideDialog({
  open,
  conflict,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  conflict: AppointmentConflictPayload | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const t = useTranslations("appointments.conflicts");
  const [reason, setReason] = useState("");
  // Reset on the way out (cancel / confirm) rather than in an effect on open.
  const cancel = () => {
    setReason("");
    onCancel();
  };
  const confirm = () => {
    const value = reason.trim();
    setReason("");
    onConfirm(value);
  };

  const canOverride = conflict?.override_allowed ?? false;
  const reasonOk = reason.trim().length >= MIN_REASON;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && cancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconAlertTriangle className="size-5 text-amber-600" aria-hidden />
            {t("title")}
          </DialogTitle>
          <DialogDescription>{canOverride ? t("body") : t("cannotOverride")}</DialogDescription>
        </DialogHeader>

        <ConflictList conflicts={conflict?.conflicts ?? []} />

        {canOverride && (
          <div className="grid gap-1.5">
            <Label htmlFor="conflict-override-reason">{t("reasonLabel")}</Label>
            <Textarea
              id="conflict-override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("reasonPlaceholder")}
              rows={3}
              aria-describedby="conflict-override-hint"
            />
            <p id="conflict-override-hint" className="text-xs text-muted-foreground">
              {reasonOk ? t("reasonLogged") : t("reasonTooShort", { min: MIN_REASON })}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={cancel}>
            {canOverride ? t("cancel") : t("close")}
          </Button>
          {canOverride && (
            <Button type="button" disabled={!reasonOk} onClick={confirm}>
              {t("bookAnyway")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The clash list shared by the dialog (drag) and the inline panel (sheet). */
export function ConflictList({ conflicts }: { conflicts: AppointmentConflict[] }) {
  const t = useTranslations("appointments.conflicts");
  const format = useFormatter();
  return (
    <ul className="divide-y rounded-md border text-sm" aria-label={t("listAria")}>
      {conflicts.map((c) => (
        <li key={`${c.kind}:${c.appointment_id}`} className="flex flex-col gap-0.5 px-3 py-2">
          <span className="font-medium">
            {c.kind === "doctor"
              ? t("doctorKind", { name: c.user?.full_name ?? "—" })
              : t("patientKind", { name: c.patient?.full_name ?? "—" })}
          </span>
          <span className="tabular-nums text-muted-foreground">
            {format.dateTime(new Date(c.starts_at), { dateStyle: "medium", timeStyle: "short" })}
            {" – "}
            {format.dateTime(new Date(c.ends_at), { timeStyle: "short" })}
            {c.title ? ` · ${c.title}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 86-01: the same 409 rendered INLINE inside the appointment sheet's edit
 * body — the conflicts sit right under the time the desk just picked, with
 * the reason box and "Book anyway" in place, instead of a second dialog over
 * the sheet.
 */
export function ConflictOverridePanel({
  conflict,
  onDismiss,
  onConfirm,
  busy = false,
}: {
  conflict: AppointmentConflictPayload;
  onDismiss: () => void;
  onConfirm: (reason: string) => void;
  busy?: boolean;
}) {
  const t = useTranslations("appointments.conflicts");
  const [reason, setReason] = useState("");
  const canOverride = conflict.override_allowed;
  const reasonOk = reason.trim().length >= MIN_REASON;

  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-[10px] border border-amber-500/40 bg-amber-500/5 p-3.5"
    >
      <div className="flex items-start gap-2">
        <IconAlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-semibold">{t("title")}</p>
          <p className="text-xs text-muted-foreground">
            {canOverride ? t("body") : t("cannotOverride")}
          </p>
        </div>
      </div>
      <ConflictList conflicts={conflict.conflicts} />
      {canOverride && (
        <div className="grid gap-1.5">
          <Label htmlFor="conflict-override-reason-inline">{t("reasonLabel")}</Label>
          <Textarea
            id="conflict-override-reason-inline"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("reasonPlaceholder")}
            rows={2}
            aria-describedby="conflict-override-hint-inline"
          />
          <p id="conflict-override-hint-inline" className="text-xs text-muted-foreground">
            {reasonOk ? t("reasonLogged") : t("reasonTooShort", { min: MIN_REASON })}
          </p>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDismiss} disabled={busy}>
          {canOverride ? t("cancel") : t("close")}
        </Button>
        {canOverride && (
          <Button
            type="button"
            size="sm"
            disabled={!reasonOk || busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {t("bookAnyway")}
          </Button>
        )}
      </div>
    </div>
  );
}
